import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  TextField,
  Button,
  Alert,
  Select,
  MenuItem,
  Stack,
  Chip,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import DataTable, { Column } from '../../components/common/DataTable';
import StatusChip from '../../components/common/StatusChip';
import { getUsers } from '../../services/api/userApi';
import { User, UserFilters, UserSortField } from '../../types/user';
import { formatDateTime } from '../../utils/formatters';

const UserListPage: React.FC = () => {
  const navigate = useNavigate();
  const defaults: UserFilters = { page: 0, limit: 20, orderBy: 'createdAt', order: 'desc' };
  const [filters, setFilters] = useState<UserFilters>(defaults);
  const [draft, setDraft] = useState<UserFilters>(defaults);
  const invalidDates = !!(
    draft.createdFrom &&
    draft.createdTo &&
    draft.createdFrom > draft.createdTo
  );
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['users', filters],
    queryFn: () => getUsers(filters),
  });
  const updateDraft = (key: keyof UserFilters, value: string | boolean | undefined) => {
    setDraft(prev => ({ ...prev, [key]: value === '' ? undefined : value }));
  };
  const applyFilters = (event: React.FormEvent) => {
    event.preventDefault();
    if (!invalidDates)
      setFilters({
        ...draft,
        page: 0,
        limit: filters.limit,
        orderBy: filters.orderBy,
        order: filters.order,
      });
  };
  const resetFilters = () => {
    const next = { ...defaults, limit: filters.limit };
    setDraft(next);
    setFilters(next);
  };
  const handleSort = (column: string) => {
    setFilters(prev => ({
      ...prev,
      page: 0,
      orderBy: column as UserSortField,
      order: prev.orderBy === column && prev.order === 'asc' ? 'desc' : 'asc',
    }));
  };
  const handlePageChange = (page: number) => setFilters(prev => ({ ...prev, page }));
  const handleRowsPerPageChange = (limit: number) =>
    setFilters(prev => ({ ...prev, limit, page: 0 }));
  const handleRowClick = (user: User) => navigate(`/users/${user.id}`);
  const selectFilter = (
    key: keyof UserFilters,
    label: string,
    options: [string, string][],
    boolean = false
  ) => (
    <FormControl size="small" fullWidth>
      <InputLabel id={`${key}-label`}>{label}</InputLabel>
      <Select
        labelId={`${key}-label`}
        label={label}
        value={draft[key] === undefined ? '' : String(draft[key])}
        onChange={e =>
          updateDraft(
            key,
            e.target.value === '' ? undefined : boolean ? e.target.value === 'true' : e.target.value
          )
        }
      >
        <MenuItem value="">전체</MenuItem>
        {options.map(([value, text]) => (
          <MenuItem key={value} value={value}>
            {text}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  const columns: Column<User>[] = [
    {
      id: 'email',
      label: '이메일',
      minWidth: 200,
      sortable: true,
    },
    {
      id: 'username',
      label: '사용자명',
      minWidth: 150,
      sortable: true,
    },
    {
      id: 'provider',
      sortable: true,
      label: '로그인 방식',
      minWidth: 120,
      align: 'center',
      format: (value: string) => {
        const providerMap: Record<
          string,
          {
            label: string;
            color: 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';
          }
        > = {
          google: { label: 'Google', color: 'error' },
          kakao: { label: 'Kakao', color: 'warning' },
          local: { label: '일반', color: 'default' },
          apple: { label: 'Apple', color: 'default' },
        };
        const provider = providerMap[value] || providerMap['local'];
        return (
          <StatusChip
            status={value === 'google' ? 'error' : value === 'kakao' ? 'warning' : 'inactive'}
            label={provider.label}
          />
        );
      },
    },
    {
      id: 'isAdmin',
      sortable: true,
      label: '관리자',
      minWidth: 100,
      align: 'center',
      format: (value: boolean) => (
        <StatusChip status={value ? 'active' : 'inactive'} label={value ? '관리자' : '일반'} />
      ),
    },
    {
      id: 'isSubscribed',
      sortable: true,
      label: '구독 상태',
      minWidth: 120,
      align: 'center',
      format: (value: boolean, row: User) => {
        if (!value) {
          return <StatusChip status="inactive" label="미구독" />;
        }
        // 구독 만료 확인
        const expiresAt = row.subscriptionExpiresAt ? new Date(row.subscriptionExpiresAt) : null;
        const isExpired = expiresAt && expiresAt < new Date();

        if (isExpired) {
          return <StatusChip status="inactive" label="만료" />;
        }

        // 등급별 색상 및 이름 매핑 (모바일 앱과 동일한 색상)
        const tierConfig: Record<string, { label: string; color: string; textColor: string }> = {
          starter: { label: '🌱 스타터', color: '#8BC34A', textColor: '#fff' },
          basic: { label: '🛡️ 베이직', color: '#2196F3', textColor: '#fff' },
          silver: { label: '🥈 실버', color: '#C0C0C0', textColor: '#333' },
          gold: { label: '🥇 골드', color: '#FFD700', textColor: '#333' },
          platinum: { label: '💎 플래티넘', color: '#00D9FF', textColor: '#fff' },
          diamond: { label: '💠 다이아몬드', color: '#B9F2FF', textColor: '#333' },
        };

        const tier = row.subscriptionTier || 'starter';
        const config = tierConfig[tier] || {
          label: '✅ 구독중',
          color: '#8BC34A',
          textColor: '#fff',
        };

        return (
          <Chip
            label={config.label}
            size="small"
            sx={{
              backgroundColor: config.color,
              color: config.textColor,
              fontWeight: 500,
            }}
          />
        );
      },
    },
    {
      id: 'notificationEnabled',
      sortable: true,
      label: '알림 설정',
      minWidth: 100,
      align: 'center',
      format: (value: boolean) => (
        <StatusChip status={value ? 'active' : 'inactive'} label={value ? '활성' : '비활성'} />
      ),
    },
    {
      id: 'blockMalware',
      sortable: true,
      label: '악성앱 차단',
      minWidth: 120,
      align: 'center',
      format: (value: boolean) => (
        <StatusChip status={value ? 'active' : 'inactive'} label={value ? '활성' : '비활성'} />
      ),
    },
    {
      id: 'createdAt',
      label: '가입일',
      minWidth: 150,
      format: (value: Date) => formatDateTime(value),
      sortable: true,
    },
  ];

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        사용자 관리
      </Typography>

      <Paper component="form" onSubmit={applyFilters} sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
          사용자 검색 및 필터
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' },
            gap: 2,
          }}
        >
          <TextField
            size="small"
            label="이메일"
            placeholder="이메일 일부 입력"
            value={draft.email || ''}
            onChange={e => updateDraft('email', e.target.value)}
            inputProps={{ maxLength: 200 }}
          />
          <TextField
            size="small"
            label="사용자명"
            placeholder="사용자명 일부 입력"
            value={draft.username || ''}
            onChange={e => updateDraft('username', e.target.value)}
            inputProps={{ maxLength: 200 }}
          />
          {selectFilter('provider', '로그인 방식', [
            ['local', '일반'],
            ['google', 'Google'],
            ['kakao', 'Kakao'],
            ['apple', 'Apple'],
          ])}
          {selectFilter(
            'isAdmin',
            '관리자',
            [
              ['true', '관리자'],
              ['false', '일반 사용자'],
            ],
            true
          )}
          {selectFilter('subscriptionStatus', '구독 상태', [
            ['active', '구독 중'],
            ['expired', '만료'],
            ['none', '미구독'],
          ])}
          {selectFilter(
            'notificationEnabled',
            '알림 설정',
            [
              ['true', '활성'],
              ['false', '비활성'],
            ],
            true
          )}
          {selectFilter(
            'blockMalware',
            '악성앱 차단',
            [
              ['true', '활성'],
              ['false', '비활성'],
            ],
            true
          )}
          <Box sx={{ display: { xs: 'none', lg: 'block' } }} />
          <TextField
            size="small"
            label="가입일 시작"
            type="date"
            value={draft.createdFrom || ''}
            onChange={e => updateDraft('createdFrom', e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ max: draft.createdTo }}
          />
          <TextField
            size="small"
            label="가입일 종료"
            type="date"
            value={draft.createdTo || ''}
            onChange={e => updateDraft('createdTo', e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: draft.createdFrom }}
            error={invalidDates}
            helperText={
              invalidDates
                ? '종료일은 시작일 이후여야 합니다.'
                : '선택한 종료일까지 포함 · 한국시간'
            }
          />
        </Box>
        <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
          <Button variant="contained" type="submit" disabled={invalidDates}>
            필터 적용
          </Button>
          <Button variant="outlined" onClick={resetFilters}>
            초기화
          </Button>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          여러 조건을 함께 적용할 수 있습니다. 열 제목을 누르면 오름차순·내림차순으로 정렬합니다.
        </Typography>
      </Paper>
      {isError && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" onClick={() => refetch()}>
              다시 시도
            </Button>
          }
        >
          사용자 목록을 불러오지 못했습니다.
        </Alert>
      )}

      <DataTable
        columns={columns}
        data={data?.items || []}
        total={data?.total || 0}
        page={filters.page}
        rowsPerPage={filters.limit}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
        onRowClick={handleRowClick}
        loading={isLoading}
        orderBy={filters.orderBy}
        order={filters.order}
        onSort={handleSort}
      />
    </Box>
  );
};

export default UserListPage;
